import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../hooks/api.js';
import { parseEmailDocument, type EmailDocument } from '@blogforge/shared';

interface Props {
  onClose: () => void;
}

export function ImportEmailModal({ onClose }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [input, setInput] = useState('');
  const [error, setError] = useState('');

  const importMutation = useMutation({
    // The document as parsed — every field the export writes, so a file
    // exported from BlogForger comes back whole (see EmailDocumentSchema).
    mutationFn: async (document: EmailDocument) =>
      (await api.emails.create(document)) as { id: string },
    onSuccess: (email) => {
      void qc.invalidateQueries({ queryKey: ['emails'] });
      navigate(`/emails/${email.id}/edit`);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  /**
   * Accepts either a bare blocks array or a full email object — the same
   * document the export writes, read by the same schema. The parsing itself
   * lives in @blogforge/shared (parseEmailDocument) so this dialog and the API
   * cannot disagree about what a file may contain.
   */
  const parseImport = (text: string): EmailDocument | { error: string } => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Invalid JSON format' };
    }

    const result = parseEmailDocument(parsed);
    return result.ok ? result.document : { error: result.error };
  };

  const handleImport = () => {
    setError('');
    const result = parseImport(input);
    if ('error' in result) {
      setError(result.error);
      return;
    }
    importMutation.mutate(result);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        setInput(e.target?.result as string);
        setError('');
      } catch {
        setError('Failed to read file');
      }
    };
    reader.onerror = () => setError('Failed to read file');
    reader.readAsText(file);

    // Reset the input so the same file can be uploaded again
    event.target.value = '';
  };

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (!importMutation.isPending) onClose();
      }}
    >
      <div className="modal-card w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-ink text-[17px] font-bold tracking-tight">Import email</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={importMutation.isPending}
            className="text-muted hover:bg-card-2 hover:text-ink-soft rounded p-1 disabled:opacity-40"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <p className="text-ink-soft text-sm">
            Upload a JSON file or paste valid JSON to create a <strong>new</strong> email. Accepts a
            blocks array or a full email object (subject, previewText, designOverrides, blocks).
          </p>
        </div>

        {/* File upload */}
        <div className="mb-4">
          <label className="field-label">Upload JSON file</label>
          <input
            type="file"
            accept=".json,application/json"
            onChange={handleFileUpload}
            className="text-muted file:bg-accent/10 file:text-primary hover:file:bg-accent/20 mt-1 block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:px-4 file:py-2 file:text-sm file:font-semibold"
          />
        </div>

        <div className="mb-4">
          <label className="field-label">Or paste JSON</label>
          <textarea
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setError('');
            }}
            rows={10}
            className={`bg-bg-soft mt-1.5 w-full rounded-xl border px-3 py-2.5 font-mono text-[13px] leading-relaxed ${
              error
                ? 'border-danger/30 focus:border-danger/50 focus:ring-danger/25 focus:ring-2'
                : 'border-line-strong focus:border-accent-border focus:ring-accent/20 focus:ring-2'
            }`}
            placeholder='{"subject": "My imported email", "designOverrides": {"colorBackground": "#0F172A"}, "blocks": [{"id": "hello", "type": "heading", "content": "Hello", "attrs": {"level": 1}}]}'
          />
        </div>

        {error && (
          <div className="bg-danger/8 text-danger mb-4 rounded-md p-3 text-sm">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={importMutation.isPending}
            className="btn-secondary rounded-lg px-4 py-2 text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={!input.trim() || importMutation.isPending}
            className="btn-primary rounded-lg px-4 py-2 text-sm disabled:opacity-50"
          >
            {importMutation.isPending ? 'Importing…' : 'Import & open'}
          </button>
        </div>

        <div className="border-line mt-4 border-t pt-4">
          <details className="text-ink-soft text-sm">
            <summary className="hover:text-ink cursor-pointer">Expected JSON format</summary>
            <pre className="bg-bg-soft mt-2 rounded-md p-3 text-xs">{`[
  {
    "id": "block-1",
    "type": "text",
    "content": "Hello world",
    "style": { "fontFamily": "Arial" }
  }
]`}</pre>
          </details>
        </div>
      </div>
    </div>
  );
}
